import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Table,
  Alert,
  Typography,
  Button,
  Dropdown,
  MenuProps,
  message,
  Card,
  Breadcrumb,
  Modal,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  MailOutlined,
  MoreOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Item } from "../../../types/index";
import type { ColumnsType } from "antd/es/table";
import { END_POINT } from "config/environment";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { saveAs } from "file-saver";
import { EventSourcePolyfill } from "event-source-polyfill";
import { useNavigate } from "react-router-dom";
import WarningModal from "components/layout/components/WarningModal";
import { routePath } from "config/routeMgt/routePath";
import CVDetailsPage from "./CVDetailsPage";

const { Text, Title } = Typography;

interface ItemDetailsPageProps {
  item: Item | null;
  analysisResults: { [key: string]: any[] };
  onBack?: () => void;
}

interface ProcessedCandidate {
  key: any;
  talent: string;
  filename: string;
  fit: string;
  reason: string;
  skills: any;
  years: string;
  strengths: any;
  weaknesses: any;
  summary: string;
  rawData: any;
  id?: any;
  status?: any;
  education?: string;
  workExperience?: string;
  reviewFlags?: string[];
  skillGaps?: string[];
  achievements?: string[];
  essentialRequirementFlag?: string;
  jobSummary?: string;
}

const safeJsonParse = (value: any, fallback: any[] = []): any[] => {
  try {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string" && value.trim()) {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    }

    return fallback;
  } catch (e) {
    console.error("JSON parse error:", e, "Value:", value);
    return fallback;
  }
};

const formatExperience = (months: number): string => {
  if (!months || months <= 0) return "0 years";
  const years = Math.round(months / 12);
  return `${years} year${years !== 1 ? "s" : ""}`;
};

const formatYearsFromProfile = (data: any): string => {
  if (
    data?.relevantYearsOfExperience !== undefined &&
    data?.relevantYearsOfExperience !== null &&
    data?.relevantYearsOfExperience !== ""
  ) {
    const years = Number(data.relevantYearsOfExperience);
    if (!Number.isNaN(years)) {
      return `${years.toFixed(1)} year${years === 1 ? "" : "s"}`;
    }
  }

  return formatExperience(data?.total_experience || 0);
};

const formatWorkExperienceEntries = (experience: any[]): string => {
  if (!experience || !Array.isArray(experience) || experience.length === 0) {
    return "No work experience available";
  }

  return experience
    .map((exp: any) => {
      const title = exp.title || "Position";
      const company = exp.company || "Company";
      const startDate = exp.startDate || exp.start_date || "";
      const endDate = exp.endDate || exp.end_date || "";
      const dates = [startDate, endDate].filter(Boolean).join(" - ");

      return dates
        ? `• ${title} at ${company}\n  ${dates}`
        : `• ${title} at ${company}`;
    })
    .join("\n\n");
};

const formatEducationEntries = (education: any[]): string => {
  if (!education || !Array.isArray(education) || education.length === 0) {
    return "No education information available";
  }

  return education
    .map((edu: any) => {
      const degree = edu.degree || "Degree";
      const school = edu.school || edu.institution || "";
      const startDate = edu.startDate || edu.start_date || "";
      const endDate = edu.endDate || edu.end_date || edu.year || "";
      const fieldOfStudyRaw = edu.fieldOfStudy ?? edu.field_of_study ?? "";
      const fieldOfStudy =
        fieldOfStudyRaw && fieldOfStudyRaw !== "Not available"
          ? String(fieldOfStudyRaw).trim()
          : "";
      const dates = [startDate, endDate].filter(Boolean).join(" - ");
const degreeWithField = [degree, fieldOfStudy].filter(Boolean).join(", ");
const label = school ? `${degreeWithField} - ${school}` : degreeWithField;
      return dates ? `• ${label}\n  ${dates}` : `• ${label}`;
    })
    .join("\n\n");
};

const parseStringList = (value: any): string[] => {
  const parsed = safeJsonParse(value, []);

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return [];
  }

  return parsed
    .map((item) =>
      typeof item === "string" ? item.trim() : String(item ?? ""),
    )
    .filter(Boolean);
};

const parseEssentialRequirementFlag = (value: any): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "string" ? item.trim() : String(item ?? ""),
      )
      .filter(Boolean)
      .join("; ");
  }

  if (typeof value === "string") {
    return value.trim();
  }

  return String(value);
};

const SCAN_RESULTS_PAGE_SIZE = 10;

const formatScore = (score: number): string => {
  return score ? `${Math.round(score)}%` : "-";
};

const getFitScore = (candidate: { fit?: string }): number => {
  const parsed = parseInt(String(candidate.fit ?? ""), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortCandidatesByFitDesc = (
  candidates: ProcessedCandidate[],
): ProcessedCandidate[] =>
  [...candidates].sort((a, b) => getFitScore(b) - getFitScore(a));

const formatSummaryText = (summary: string): string[] | string => {
  if (!summary || summary === "No summary available") {
    return ["No summary available"];
  }

  try {
    const parsed = JSON.parse(summary);

    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (e) {
    // Not JSON, continue with string parsing
  }

  const sentences = summary.split(/\.\s+/).filter((s) => s.trim().length > 0);

  if (sentences.length > 1) {
    return sentences.map((s) => s.trim() + ".");
  }

  const lines = summary.split(/\n+/).filter((s) => s.trim().length > 0);

  if (lines.length > 1) {
    return lines.map((s) => s.trim());
  }

  return summary;
};

const USE_MOCK_CANDIDATES = false;

const mockProfiles = [
  {
    id: "4be81f3b-e6e4-4d52-b06d-81c20dd91282",
    chatId: "4176e963-32e7-4e74-ae9c-07bc3bac9833",
    email: "adenikefoster@gmail.com",
    phone: "",
    name: "ADENIKE FOSTER",
    summary:
      "ADENIKE FOSTER has a total score of 38.0, indicating a Low Fit for the Chief Technology Officer role. The candidate brings experience within the Fintech industry, demonstrated by roles at FIS Securities Limited and Access Bank Plc, and holds relevant certifications such as Authorized Dealing Clerk. However, significant gaps exist in the required job family and specific technical leadership skills for a CTO position. The candidate's 0.6 years of relevant experience is considerably below the expectations for a senior technology leadership role. Overall, the candidate's background is not directly aligned with the core technical and strategic requirements of this position.",
    skills: "[]",
    experience: JSON.stringify([
      {
        title: "TRADER",
        company: "FIS Securities Limited",
        startDate: "Jan 2021",
        endDate: "Present",
        date_status: "consistent",
        relevant_years_contribution: 0,
        job_family_category: "unrelated",
        description: null,
        duration: null,
      },
      {
        title: "Contract staff",
        company: "Access Bank Plc",
        startDate: "Mar 2019",
        endDate: "Feb 2020",
        date_status: "consistent",
        relevant_years_contribution: 0,
        job_family_category: "unrelated",
        description: null,
        duration: null,
      },
      {
        title: "Business Development",
        company: "OneSoft IT Solutions",
        startDate: "Nov 2016",
        endDate: "Dec 2017",
        date_status: "consistent",
        relevant_years_contribution: 0.55,
        job_family_category: "adjacent",
        description: null,
        duration: null,
      },
    ]),
    workExperience: JSON.stringify([
      {
        title: "TRADER",
        company: "FIS Securities Limited",
        startDate: "Jan 2021",
        endDate: "Present",
      },
      {
        title: "Contract staff",
        company: "Access Bank Plc",
        startDate: "Mar 2019",
        endDate: "Feb 2020",
      },
      {
        title: "Business Development",
        company: "OneSoft IT Solutions",
        startDate: "Nov 2016",
        endDate: "Dec 2017",
      },
    ]),
    education: JSON.stringify([
      {
        school: "University of Lagos",
        degree: "MSc",
        startDate: "Not available",
        endDate: "Not available",
        description: "with ACIB",
      },
      {
        school: "University of Lagos",
        degree: "BSc",
        startDate: "Oct 2010",
        endDate: "Feb 2015",
        description: "Not available",
      },
    ]),
    factorScores: JSON.stringify({
      achievements: "10.5/21",
      experience_depth: "6.5/20",
      skills_match: "1.9/18",
      job_level: "4/12",
      job_family: "0/10",
      industry: "7/7",
      education: "4.2/5",
      certifications: "3/5",
      keywords: "1/2",
    }),
    reviewFlags: "[]",
    score: 38,
    experienceLevel: "Low Fit",
    confidenceLevel: "High",
    strengths: JSON.stringify([
      "industry: 7/7 — Same industry experience",
      "education: 4/5 — Related field of study",
      "certifications: 3/5 — Relevant certifications held (not required)",
    ]),
    weaknesses: JSON.stringify([
      "job_family: 0/10 — Different job family — no adjacency identified",
      "skills_match: 2/18 — One or more must-have skills not evidenced; No must-have skills evidenced",
      "experience_depth: 6/20 — Candidate significantly below minimum experience requirement; Low proportion of experience is directly relevant to the role",
      "job_level: 4/12 — Raw level evidence is weak for target grade",
    ]),
    ranking_reasons:
      "Score: 38/100 (Low Fit) — Credible achievements described but not quantified. Top strength: industry: 7/7 — Same industry experience",
    bestSuitedFor: "",
    total_experience: 7,
    relevantYearsOfExperience: 0.6,
    linkedinUrl: null,
    githubUrl: null,
    filename:
      "https://d1k5i283q2yiu2.cloudfront.net/smartscreen/4176e963-32e7-4e74-ae9c-07bc3bac9833/profiles/4be81f3b-e6e4-4d52-b06d-81c20dd91282/original_ADENIKE_FOSTER_CV.pdf",
    created_at: "2026-08-01T10:39:03.760719Z",
  },

  {
    id: "b44ed366-1e6f-4f42-974a-c37dd3daba68",
    chatId: "4176e963-32e7-4e74-ae9c-07bc3bac9833",
    email: "contactniran@yahoo.com",
    phone: "",
    name: "OLALEMI Adeniran Jacob",
    summary:
      "The candidate received a total score of 28.0, indicating a Low Fit for the Chief Technology Officer role. They bring a strong educational background in Accounting and relevant certifications such as ICAN and CITN, along with experience in adjacent industries like microfinance. However, a significant gap exists in direct technology leadership and software development experience, as evidenced by 0.0 years of relevant experience for a CTO position.",
    skills: "[]",
    experience: JSON.stringify([
      {
        title: "Assistant Manager",
        company: "KPMG Professional Services",
        startDate: "Apr 2022",
        endDate: "Present",
      },
      {
        title: "Team Lead, Financial Reporting, Tax and Regulatory Compliance",
        company: "Renmoney Microfinance Bank Limited",
        startDate: "Mar 2017",
        endDate: "Mar 2022",
      },
      {
        title: "Audit and Tax Associate",
        company: "La Bonte and Associates",
        startDate: "Nov 2014",
        endDate: "Feb 2017",
      },
    ]),
    workExperience: JSON.stringify([
      {
        title: "Assistant Manager",
        company: "KPMG Professional Services",
        startDate: "Apr 2022",
        endDate: "Present",
      },
      {
        title: "Team Lead, Financial Reporting, Tax and Regulatory Compliance",
        company: "Renmoney Microfinance Bank Limited",
        startDate: "Mar 2017",
        endDate: "Mar 2022",
      },
    ]),
    education: JSON.stringify([
      {
        school: "Obafemi Awolowo University",
        degree: "Masters of Business Administration",
        startDate: "2015",
        endDate: "2017",
        description: "Not available",
      },
      {
        school: "Obafemi Awolowo University",
        degree: "B.Sc.",
        startDate: "2008",
        endDate: "2013",
        description: "[ Second Class, Upper division ]",
      },
    ]),
    factorScores: JSON.stringify({
      achievements: "3.1/21",
      experience_depth: "1/20",
      skills_match: "1.7/18",
      job_level: "10/12",
      job_family: "0.4/10",
      industry: "4.9/7",
      education: "4.2/5",
      certifications: "3/5",
      keywords: "0/2",
    }),
    reviewFlags: "[]",
    score: 28,
    experienceLevel: "Low Fit",
    confidenceLevel: "High",
    strengths: JSON.stringify([
      "education: 4/5 — Related field of study",
      "job_level: 10/12 — Raw level evidence strongly matches target grade",
      "industry: 5/7 — Adjacent industry experience",
      "certifications: 3/5 — Relevant certifications held (not required)",
    ]),
    weaknesses: JSON.stringify([
      "keywords: 0/2",
      "job_family: 0/10 — Different job family — no adjacency identified",
      "experience_depth: 1/20 — No relevant experience identified",
      "skills_match: 2/18 — One or more must-have skills not evidenced",
    ]),
    ranking_reasons:
      "Score: 28/100 (Low Fit) — CV lists duties only with no measurable accomplishments.",
    total_experience: 0,
    relevantYearsOfExperience: 0,
    linkedinUrl: null,
    githubUrl: null,
    filename:
      "https://d1k5i283q2yiu2.cloudfront.net/smartscreen/4176e963-32e7-4e74-ae9c-07bc3bac9833/profiles/b44ed366-1e6f-4f42-974a-c37dd3daba68/original_Adeniran_Olalemi-_Resume.pdf",
    created_at: "2026-08-01T10:40:04.627587Z",
  },
];

const ItemDetailsPage: React.FC<ItemDetailsPageProps> = ({
  item,
  analysisResults,
  onBack,
}) => {
  const navigate = useNavigate();

  const [dataSource, setDataSource] = useState<ProcessedCandidate[]>([]);
  const [hasResults, setHasResults] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [candidateToDelete, setCandidateToDelete] =
    useState<ProcessedCandidate | null>(null);
  const [selectedCandidate, setSelectedCandidate] =
    useState<ProcessedCandidate | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [deletingIds, setDeletingIds] = useState<number[]>([]);
  const { token } = useGetUserInfo();
  const profileEventSourceRef = useRef<EventSource | null>(null);

  const [isExporting, setIsExporting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [storedItem, setStoredItem] = useState<Item | null>(null);
  const [storedAnalysisResults, setStoredAnalysisResults] = useState<{
    [key: string]: any[];
  }>({});

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: SCAN_RESULTS_PAGE_SIZE,
  });

  const [serverProfilesPagination, setServerProfilesPagination] =
    useState(true);

  const [profilesTotal, setProfilesTotal] = useState(0);

  const paginationRef = useRef(pagination);
  const serverPaginatedRef = useRef(true);
  const profileStreamDoneRef = useRef(false);

  const [isMobileView, setIsMobileView] = useState(false);
  const [showCVDetailsPage, setShowCVDetailsPage] = useState(false);

  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState("");
  const [isAchievementsModalVisible, setIsAchievementsModalVisible] =
    useState(false);
  const [selectedAchievements, setSelectedAchievements] = useState<string[]>(
    [],
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    serverPaginatedRef.current = serverProfilesPagination;
  }, [serverProfilesPagination]);

  useEffect(() => {
    if (!serverProfilesPagination) {
      const totalPages = Math.ceil(dataSource.length / pagination.pageSize);

      if (pagination.current > totalPages && totalPages > 0) {
        setPagination((prev) => ({
          ...prev,
          current: totalPages,
        }));
      }
    }
  }, [
    dataSource.length,
    pagination.pageSize,
    pagination.current,
    serverProfilesPagination,
  ]);

  useEffect(() => {
    if (item) {
      setStoredItem(item);
      setStoredAnalysisResults(analysisResults);
    }
  }, [item, analysisResults]);

  const displayItem = storedItem || item;

  const processCandidateData = useCallback(
    (data: any, index: any): ProcessedCandidate => {
      const parseWithFallback = (value: any, fallback: string) => {
        if (!value || value.trim?.() === "") {
          return [fallback];
        }

        const parsed = safeJsonParse(value);

        return Array.isArray(parsed) && parsed.length > 0 ? parsed : [fallback];
      };

      const skills = parseWithFallback(data.skills, "No Skill Found");

      const strengths = parseWithFallback(data.strengths, "No Strength Found");

      const weaknesses = parseWithFallback(
        data.weaknesses,
        "No Weakness Found",
      );

      const reviewFlags = parseStringList(data.reviewFlags);

      const skillGaps = parseStringList(data.skillsGaps ?? data.skillGaps);

      const achievements = parseStringList(data.achievements);

      const essentialRequirementFlag = parseEssentialRequirementFlag(
        data.essentialRequirementFlag,
      );

      const isRanked = data.score;

      const jobSummary =
        data.jobSummary || data.summary || "No summary available";

      const parsedEducation = safeJsonParse(data.education, []);

      const parsedWorkExperience = safeJsonParse(
        data.workExperience || data.experience,
        [],
      );

      return {
        key: data.id,
        talent: data.name || `Candidate ${index + 1}`,
        filename: data.filename || "No File Name",
        fit: isRanked ? formatScore(data.score) : "0%",
        reason:
          data.ranking_reasons ||
          data.rankingReasons ||
          (isRanked ? "Not ranked yet" : "Processing..."),
        skills: Array.isArray(skills) ? skills : [],
        years: formatYearsFromProfile(data),
        strengths: Array.isArray(strengths) ? strengths : [],
        weaknesses: Array.isArray(weaknesses) ? weaknesses : [],
        summary: data.summary || "No summary available",
        rawData: data,
        id: data.id,
        status: data.experienceLevel ?? "",
        education: formatEducationEntries(parsedEducation),
        workExperience: formatWorkExperienceEntries(parsedWorkExperience),
        reviewFlags,
        skillGaps,
        achievements,
        essentialRequirementFlag,
        jobSummary,
      };
    },
    [],
  );

  const fetchChatHistoryDetailsPage = useCallback(
    async (page: number, pageSize: number) => {
      const chatId = displayItem?.id;

      if (!chatId || !token) return;

      setIsLoading(true);

      try {
        const res = await fetch(
          `${END_POINT.BASE_URL}/v1/chat-history-details?chat_id=${encodeURIComponent(
            String(chatId),
          )}&page=${page}&per_page=${pageSize}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          },
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const json = await res.json();

        const pageData = json.profiles ?? {};

        const profs: any[] = Array.isArray(pageData.data) ? pageData.data : [];

        const currentPage = pageData.current_page ?? page;

        const perPage = pageData.per_page ?? pageSize;

        const offset = (currentPage - 1) * perPage;

        const formatted = profs.map((p: any, i: number) =>
          processCandidateData(p, offset + i),
        );

        setDataSource(formatted);

        setProfilesTotal(
          typeof pageData.total === "number" ? pageData.total : 0,
        );

        setHasResults(
          (typeof pageData.total === "number" ? pageData.total : profs.length) >
            0,
        );
      } catch (e) {
        console.error("Error fetching chat history details:", e);

        setDataSource([]);
        setProfilesTotal(0);
        setHasResults(false);
      } finally {
        setIsLoading(false);
      }
    },
    [displayItem?.id, token, processCandidateData],
  );

  const fetchCurrentProfiles = useCallback(async () => {
    const { current, pageSize } = paginationRef.current;

    await fetchChatHistoryDetailsPage(current, pageSize);
  }, [fetchChatHistoryDetailsPage]);

  useEffect(() => {
    if (!displayItem) return;
    if (USE_MOCK_CANDIDATES) {
      const mockData = mockProfiles.map((profile, index) =>
        processCandidateData(profile, index),
      );

      setServerProfilesPagination(false);
      setDataSource(mockData);
      setHasResults(mockData.length > 0);
      setProfilesTotal(mockData.length);

      return;
    }

    if (displayItem.profiles && displayItem.profiles.length > 0) {
      setServerProfilesPagination(false);

      const profileData = displayItem.profiles.map(
        (profile: any, index: number) => processCandidateData(profile, index),
      );

      setDataSource(profileData);
      setHasResults(true);
      setProfilesTotal(profileData.length);
    } else if (storedAnalysisResults[displayItem.id]) {
      setServerProfilesPagination(false);

      const analysisData = storedAnalysisResults[displayItem.id].map(
        (profile: any, index: number) => processCandidateData(profile, index),
      );

      setDataSource(analysisData);
      setHasResults(analysisData.length > 0);
      setProfilesTotal(analysisData.length);
    } else {
      setServerProfilesPagination(true);

      setPagination((p) => ({
        ...p,
        current: 1,
      }));

      const ps = paginationRef.current.pageSize;

      void fetchChatHistoryDetailsPage(1, ps);
    }

    if (displayItem.chatId && token) {
      const startProfileSSE = () => {
        if (profileEventSourceRef.current) {
          profileEventSourceRef.current.close();
        }

        profileStreamDoneRef.current = false;

        const eventSource = new EventSourcePolyfill(
          `${END_POINT.BASE_URL}/v1/profile-updates/${displayItem.chatId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "text/event-stream",
            },
            heartbeatTimeout: 300000,
            withCredentials: false,
          },
        );

        profileEventSourceRef.current = eventSource;

        const closeProfileStream = () => {
          profileStreamDoneRef.current = true;
          eventSource.close();

          if (profileEventSourceRef.current === eventSource) {
            profileEventSourceRef.current = null;
          }
        };

        const isBatchFinished = (payload: any) => {
          const expected = Number(payload?.expectedTotal ?? 0);
          const totalCount = Number(payload?.totalCount ?? 0);
          const processed = Number(payload?.processedCvs ?? 0);

          return Boolean(
            payload?.ready ||
            (expected > 0 && totalCount >= expected && processed >= expected),
          );
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            switch (data.type) {
              case "new_profiles": {
                if (profileStreamDoneRef.current) {
                  break;
                }

                const { current, pageSize } = paginationRef.current;

                void fetchChatHistoryDetailsPage(current, pageSize);

                if (isBatchFinished(data.data)) {
                  closeProfileStream();
                }

                break;
              }

              case "profiles_complete":
                void fetchCurrentProfiles().finally(() => {
                  closeProfileStream();
                });
                break;

              case "error":
                console.error("Profile SSE Error:", data.data);
                closeProfileStream();
                break;
            }
          } catch (error) {
            console.error("Error parsing profile SSE data:", error);
          }
        };

        eventSource.onerror = (error) => {
          console.error("Profile SSE connection error:", error);
        };
      };

      startProfileSSE();
    }

    return () => {
      if (profileEventSourceRef.current) {
        profileEventSourceRef.current.close();
        profileEventSourceRef.current = null;
      }
    };
  }, [
    displayItem,
    token,
    processCandidateData,
    storedAnalysisResults,
    fetchChatHistoryDetailsPage,
    fetchCurrentProfiles,
  ]);

  const deleteCandidate = async (candidateId: any): Promise<void> => {
    try {
      const response = await fetch(
        `${END_POINT.BASE_URL}/v1/chats/${displayItem?.id}/profiles/${candidateId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error("Error deleting candidate:", error);

      throw error;
    }
  };

  const exportCandidates = async (
    candidateIds: any[],
    format: "excel" | "pdf",
    email?: string,
  ): Promise<void> => {
    try {
      const payload = {
        profileIds: candidateIds,
        format,
        ...(email && { email }),
        chatId: displayItem?.id?.toString() || "default-chat-id",
      };

      const response = await fetch(
        `${END_POINT.BASE_URL}/v1/send-talents-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error("Error exporting candidates:", error);

      throw error;
    }
  };

  const handleBulkDelete = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;

    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} candidate(s)?`,
      content: "This action is permanent. Do you want to continue?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setIsBulkDeleting(true);

        try {
          setDeletingIds(selectedRowKeys as any[]);

          const selectedCandidates = dataSource.filter((c) =>
            selectedRowKeys.includes(c.id),
          );

          const deletePromises = selectedCandidates.map((candidate) =>
            candidate.id ? deleteCandidate(candidate.id) : Promise.resolve(),
          );

          await Promise.all(deletePromises);

          if (serverPaginatedRef.current) {
            const { current, pageSize } = paginationRef.current;

            await fetchChatHistoryDetailsPage(current, pageSize);
          } else {
            setDataSource((prev) =>
              prev.filter((row) => !selectedRowKeys.includes(row.id)),
            );
          }

          setSelectedRowKeys([]);

          message.success(
            `Deleted ${selectedCandidates.length} candidates successfully`,
          );
        } catch (err) {
          console.error(err);
          message.error("Failed to delete some candidates");
        } finally {
          setDeletingIds([]);
          setIsBulkDeleting(false);
        }
      },
    });
  }, [selectedRowKeys, dataSource, fetchChatHistoryDetailsPage]);

  const handleExport = useCallback(
    async (format: "excel" | "pdf", email?: string) => {
      try {
        const selectedCandidateIds = sortCandidatesByFitDesc(
          dataSource.filter((candidate) =>
            selectedRowKeys.includes(candidate.id),
          ),
        )
          .map((candidate) => candidate.id)
          .filter(Boolean) as any[];

        const candidateIdsToExport =
          selectedRowKeys.length > 0
            ? selectedCandidateIds
            : (sortCandidatesByFitDesc(dataSource)
                .map((candidate) => candidate.id)
                .filter(Boolean) as any[]);

        if (candidateIdsToExport.length === 0) {
          message.warning("No candidates to export");
          return;
        }

        await exportCandidates(candidateIdsToExport, format, email);

        message.success(
          `Candidates exported as ${format.toUpperCase()} successfully`,
        );
      } catch (error) {
        console.error("Error exporting candidates:", error);

        message.error("Failed to export candidates");
      }
    },
    [selectedRowKeys, dataSource],
  );

  const handleSendEmail = useCallback(() => {
    const email = prompt("Enter email address to send candidates:");

    if (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (emailRegex.test(email)) {
        handleExport("pdf", email);
      } else {
        message.error("Please enter a valid email address");
      }
    }
  }, [handleExport]);

  const downloadFileFromApi = async (
    candidateIds: any[],
    format: "excel" | "pdf",
    filenameBase?: string,
  ) => {
    setIsExporting(true);

    try {
      const payload = {
        profileIds: candidateIds,
        format,
        chatId: displayItem?.id?.toString() || "default-chat-id",
      };

      const resp = await fetch(
        `${END_POINT.BASE_URL}/v1/profiles/export-file`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!resp.ok) {
        throw new Error(`Failed to export: ${resp.status}`);
      }

      const blob = await resp.blob();

      const contentDisposition = resp.headers.get("content-disposition") || "";

      const filenameMatch =
        /filename\*=UTF-8''(.+)$/.exec(contentDisposition) ||
        /filename="?(.+)"?/.exec(contentDisposition);

      const filename =
        filenameBase ||
        (filenameMatch
          ? decodeURIComponent(filenameMatch[1])
          : `candidates.${format === "excel" ? "xlsx" : "pdf"}`);

      saveAs(blob, filename);

      message.success("Download started");
    } catch (err) {
      console.error("Download error", err);

      message.error("Failed to download file");
    } finally {
      setIsExporting(false);
    }
  };

  const handleLocalExport = useCallback(
    (format: "excel" | "pdf") => {
      const candidatesToExport = sortCandidatesByFitDesc(
        selectedRowKeys.length > 0
          ? dataSource.filter((candidate) =>
              selectedRowKeys.includes(candidate.id),
            )
          : dataSource,
      );

      if (candidatesToExport.length === 0) {
        message.warning("No candidates to export");

        return;
      }

      const ids = candidatesToExport.map((c) => c.id).filter(Boolean);

      downloadFileFromApi(
        ids,
        format,
        `candidates-${displayItem?.title || "list"}-${
          new Date().toISOString().split("T")[0]
        }.${format === "excel" ? "xlsx" : "pdf"}`,
      );
    },
    [selectedRowKeys, dataSource, displayItem],
  );

  const exportMenuItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "excel",
        label: `Download as Excel ${
          selectedRowKeys.length > 0
            ? `(${selectedRowKeys.length} selected)`
            : "(All)"
        }`,
        icon: <DownloadOutlined />,
        onClick: () => handleLocalExport("excel"),
        disabled: dataSource.length === 0 || isExporting,
      },
      {
        key: "pdf",
        label: `Download as PDF ${
          selectedRowKeys.length > 0
            ? `(${selectedRowKeys.length} selected)`
            : "(All)"
        }`,
        icon: <DownloadOutlined />,
        onClick: () => handleLocalExport("pdf"),
        disabled: dataSource.length === 0 || isExporting,
      },
      {
        key: "email",
        label: `Send via Email ${
          selectedRowKeys.length > 0
            ? `(${selectedRowKeys.length} selected)`
            : "(All)"
        }`,
        icon: <MailOutlined />,
        onClick: handleSendEmail,
        disabled: dataSource.length === 0 || isExporting,
      },
    ],
    [
      selectedRowKeys.length,
      dataSource.length,
      handleLocalExport,
      handleSendEmail,
      isExporting,
    ],
  );

  const openCVDetails = useCallback(
    (record: ProcessedCandidate) => {
      const resolvedBatchId = displayItem?.id;
      const cvDetailsPath = resolvedBatchId
        ? `${routePath.cvchat}?view=cv-details&batchId=${encodeURIComponent(
            String(resolvedBatchId),
          )}`
        : `${routePath.cvchat}?view=cv-details`;

      navigate(cvDetailsPath, {
        state: {
          selectedCandidate: record,
          selectedItem: displayItem,
        },
      });
    },
    [displayItem, navigate],
  );

  const getActionMenuItems = (
    record: ProcessedCandidate,
  ): MenuProps["items"] => [
    {
      key: "view",
      label: "View Details",
      icon: <EyeOutlined />,
      onClick: () => {
        openCVDetails(record);
      },
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        setCandidateToDelete(record);
        setDeleteModalVisible(true);
      },
    },
  ];

  const columns: ColumnsType<ProcessedCandidate> = useMemo(() => {
    const baseColumns: any[] = [
      {
        title: "Talent",
        dataIndex: "talent",
        key: "talent",
        fixed: !isMobileView ? ("left" as const) : undefined,
        width: isMobileView ? "30%" : 100,
        sorter: (a: ProcessedCandidate, b: ProcessedCandidate) =>
          a.talent.localeCompare(b.talent),
        render: (text: string, record: ProcessedCandidate) => (
          <span
            className="text-blue-600 cursor-pointer hover:underline"
            onClick={() => {
              openCVDetails(record);
            }}
          >
            {text}
          </span>
        ),
      },
      {
        title: isMobileView ? "%" : "% Fit",
        dataIndex: "fit",
        key: "fit",
        width: isMobileView ? "15%" : "8%",
        sorter: (a: ProcessedCandidate, b: ProcessedCandidate) =>
          (parseInt(a.fit) || 0) - (parseInt(b.fit) || 0),
        defaultSortOrder: "descend" as const,
      },
      {
        title: isMobileView ? "Exp" : "Years of Experience",
        dataIndex: "years",
        key: "years",
        width: isMobileView ? "15%" : 140,
      },
    ];

    if (!isMobileView) {
      baseColumns.push({
        title: "Summary",
        dataIndex: "jobSummary",
        key: "jobSummary",
        width: "12%",
        ellipsis: true,
        render: (value: string) => (
          <Tooltip title="View Summary">
            <InfoCircleOutlined
              className="text-primary cursor-pointer"
              onClick={() => {
                const summaryText = value || "No summary available";

                setSelectedSummary(summaryText);

                setIsSummaryModalVisible(true);
              }}
            />
          </Tooltip>
        ),
      });

      baseColumns.push({
        title: "Achievements",
        dataIndex: "achievements",
        key: "achievements",
        width: "10%",
        ellipsis: true,
        render: (value: string[] | undefined) => {
          const items = Array.isArray(value) ? value.filter(Boolean) : [];

          return (
            <Tooltip
              title={
                items.length > 0
                  ? "View Achievements"
                  : "No achievements listed"
              }
            >
              <InfoCircleOutlined
                className={`cursor-pointer ${
                  items.length > 0 ? "text-primary" : "text-gray-300"
                }`}
                onClick={() => {
                  setSelectedAchievements(items);
                  setIsAchievementsModalVisible(true);
                }}
              />
            </Tooltip>
          );
        },
      });

      baseColumns.push({
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status: string) => (
          <span
            className={status === "Ranked" ? "text-green-600" : "text-blue-600"}
          >
            {status || "Processing"}
          </span>
        ),
      });
    }

    baseColumns.push({
      title: "Actions",
      key: "actions",
      width: isMobileView ? "25%" : "8%",
      fixed: !isMobileView ? ("right" as const) : undefined,
      align: "center",
      render: (_: string, record: ProcessedCandidate) => (
        <Dropdown
          menu={{
            items: getActionMenuItems(record),
          }}
          placement="bottomRight"
          trigger={["click"]}
          disabled={deletingIds.includes(record.id)}
        >
          <Button
            type="text"
            icon={
              <MoreOutlined
                style={{
                  fontSize: 20,
                }}
              />
            }
            size="small"
            className="flex items-center justify-center"
          />
        </Dropdown>
      ),
    });

    return baseColumns;
  }, [deletingIds, isMobileView, displayItem, navigate, openCVDetails]);

  const hasSelection = selectedRowKeys.length > 0;

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: ProcessedCandidate) => ({
      disabled: deletingIds.includes(record.id),
    }),
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  if (!displayItem) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card>
          <Text type="secondary">No batch data available</Text>

          <div className="mt-4">
            <Button onClick={handleBack}>Go Back</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (showCVDetailsPage && selectedCandidate) {
    const rawData = selectedCandidate.rawData;

    return (
      <CVDetailsPage
        candidateData={rawData}
        onBack={() => {
          setShowCVDetailsPage(false);
          setSelectedCandidate(null);
        }}
      />
    );
  }

  return (
    <div className="flex h-screen w-full max-w-full bg-gray-50 overflow-x-hidden">
      <div className={`flex-1 ${isMobileView ? "p-3" : "p-6"} overflow-y-auto`}>
        <div
          className={`bg-white border-b shadow-sm rounded-lg mb-4 sticky top-0 z-10 ${
            isMobileView ? "" : "max-h-[140px] overflow-y-auto"
          }`}
        >
          <div className={isMobileView ? "px-3 py-3" : "px-6 py-4"}>
            <div
              className={`flex ${
                isMobileView ? "flex-col gap-3" : "items-center justify-between"
              }`}
            >
              <div
                className={`flex ${
                  isMobileView ? "items-start" : "items-center"
                } gap-3`}
              >
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBack}
                  type="text"
                  size={isMobileView ? "middle" : "large"}
                  className="!bg-blue-50 !text-blue-600 hover:!bg-blue-100"
                />

                <div>
                  <Title level={isMobileView ? 5 : 4} className="!mb-1">
                    {displayItem?.title || "Candidates"}
                  </Title>

                  {!isMobileView && (
                    <Breadcrumb
                      items={[
                        {
                          title: "Batches",
                          onClick: handleBack,
                          className: "cursor-pointer",
                        },
                        {
                          title: "Candidates",
                        },
                      ]}
                    />
                  )}
                </div>
              </div>

              <div
                className={`flex ${
                  isMobileView ? "flex-wrap gap-2" : "items-center gap-2"
                }`}
              >
                {!isMobileView && (
                  <Text type="secondary" className="mr-2">
                    {serverProfilesPagination
                      ? profilesTotal
                      : dataSource.length}{" "}
                    candidates
                    {hasSelection ? `, ${selectedRowKeys.length} selected` : ""}
                  </Text>
                )}

                {hasSelection && !isMobileView && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleBulkDelete}
                    loading={deletingIds.length > 0}
                    disabled={deletingIds.length > 0}
                  >
                    Delete Selected ({selectedRowKeys.length})
                  </Button>
                )}

                <Dropdown
                  menu={{
                    items: exportMenuItems,
                  }}
                  placement="bottomRight"
                  arrow
                  disabled={dataSource.length === 0}
                >
                  <Button
                    type="primary"
                    icon={<MoreOutlined />}
                    disabled={
                      dataSource.length === 0 || isExporting || isBulkDeleting
                    }
                    loading={isExporting}
                    size={isMobileView ? "small" : "middle"}
                  >
                    {isExporting
                      ? "Exporting..."
                      : isMobileView
                        ? "Export"
                        : `Export ${
                            hasSelection
                              ? `(${selectedRowKeys.length})`
                              : "(All)"
                          }`}
                  </Button>
                </Dropdown>
              </div>
            </div>

            {isMobileView && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <Text type="secondary" className="text-xs">
                  {serverProfilesPagination ? profilesTotal : dataSource.length}{" "}
                  candidates
                  {hasSelection ? `, ${selectedRowKeys.length} selected` : ""}
                </Text>
              </div>
            )}
          </div>
        </div>

        {!hasResults && (
          <Alert
            message="Analysis Status"
            description={
              displayItem?.status === "Processing"
                ? "The analysis is currently in progress. Please wait for results."
                : displayItem?.status === "Completed"
                  ? "Analysis completed, but no profiles are available yet."
                  : displayItem?.status === "Failed"
                    ? "The analysis failed. Please try reprocessing the batch."
                    : "No results available. The analysis may still be in progress or encountered an issue."
            }
            type={
              displayItem?.status === "Processing"
                ? "info"
                : displayItem?.status === "Failed"
                  ? "error"
                  : "warning"
            }
            showIcon
            icon={<ExclamationCircleOutlined />}
            className="mb-6"
          />
        )}

        <div className="bg-white rounded-lg shadow-sm">
          <div className={isMobileView ? "overflow-x-auto" : "overflow-auto"}>
            <div
              style={{
                minWidth: isMobileView ? "500px" : "auto",
              }}
            >
              <Table<ProcessedCandidate>
                rowKey={(record) => record.id}
                dataSource={dataSource}
                columns={columns}
                sticky={{
                  offsetHeader: 0,
                }}
                pagination={{
                  current: pagination.current,
                  pageSize: SCAN_RESULTS_PAGE_SIZE,
                  total: serverProfilesPagination
                    ? profilesTotal
                    : dataSource.length,
                  showSizeChanger: false,
                  pageSizeOptions: [String(SCAN_RESULTS_PAGE_SIZE)],
                  showQuickJumper: !isMobileView,
                  simple: isMobileView,
                  onChange: (page) => {
                    setPagination({
                      current: page,
                      pageSize: SCAN_RESULTS_PAGE_SIZE,
                    });

                    if (serverProfilesPagination) {
                      void fetchChatHistoryDetailsPage(
                        page,
                        SCAN_RESULTS_PAGE_SIZE,
                      );
                    }
                  },
                  showTotal: !isMobileView
                    ? (total, range) =>
                        `${range[0]}-${range[1]} of ${total} candidates`
                    : undefined,
                }}
                size={isMobileView ? "small" : "middle"}
                loading={isLoading}
                locale={{
                  emptyText: hasResults
                    ? "No candidate data available"
                    : "Waiting for analysis results...",
                }}
                rowSelection={isMobileView ? undefined : rowSelection}
                scroll={isMobileView ? { x: 500 } : { x: 1000 }}
              />
            </div>
          </div>
        </div>

        <Modal
          title="Candidate Summary"
          open={isSummaryModalVisible}
          onCancel={() => setIsSummaryModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsSummaryModalVisible(false)}>
              Close
            </Button>,
          ]}
          width={isMobileView ? "90%" : 600}
          styles={{
            body: {
              maxHeight: "60vh",
              overflowY: "auto",
              padding: "20px",
            },
          }}
        >
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap m-0">
            {selectedSummary || "No summary available"}
          </p>
        </Modal>

        <Modal
          title="Candidate Achievements"
          open={isAchievementsModalVisible}
          onCancel={() => setIsAchievementsModalVisible(false)}
          footer={[
            <Button
              key="close"
              onClick={() => setIsAchievementsModalVisible(false)}
            >
              Close
            </Button>,
          ]}
          width={isMobileView ? "90%" : 640}
          styles={{
            body: {
              maxHeight: "60vh",
              overflowY: "auto",
              padding: "20px",
            },
          }}
        >
          {selectedAchievements.length > 0 ? (
            <ul className="space-y-3 list-disc pl-5 m-0">
              {selectedAchievements.map((item, i) => (
                <li key={i} className="text-gray-700 leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 m-0">No achievements listed</p>
          )}
        </Modal>

        <WarningModal
          open={isDeleteModalVisible}
          title="Delete Candidate Profile"
          content={
            <>
              Are you sure you want to delete this candidate profile:{" "}
              <strong>{candidateToDelete?.talent}</strong>?
            </>
          }
          onConfirm={async () => {
            if (!candidateToDelete || !displayItem?.id) {
              return;
            }

            try {
              setDeletingIds((prev) => [...prev, candidateToDelete.id!]);

              setDataSource((prev) =>
                prev.filter((row) => row.id !== candidateToDelete.id),
              );

              await deleteCandidate(candidateToDelete.id);

              setSelectedRowKeys((prev) =>
                prev.filter((k) => k !== candidateToDelete.id),
              );

              message.success("Candidate deleted successfully");
            } catch (error) {
              console.error("Failed to delete candidate:", error);

              message.error("Failed to delete candidate");
            } finally {
              setDeletingIds((prev) =>
                prev.filter((id) => id !== candidateToDelete?.id),
              );

              setCandidateToDelete(null);

              setDeleteModalVisible(false);
            }
          }}
          onCancel={() => {
            setCandidateToDelete(null);
            setDeleteModalVisible(false);
          }}
          confirmText="Yes"
          cancelText="No"
        />
      </div>
    </div>
  );
};

export default ItemDetailsPage;
